"use strict";
/**
 * @jakguru/milicron export the {@link MiliCron} class, {@link CronTabObject} interface, and {@link EventCallback} type.
 * It also renames and exports the {@link MiliCron} class as the default export.
 * @module @jakguru/milicron
 */
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _MiliCron_instances, _a, _MiliCron_bus, _MiliCron_timeoutId, _MiliCron_running, _MiliCron_onTick, _MiliCron_crontabContraints_get, _MiliCron_aliases_get, _MiliCron_parseField;
Object.defineProperty(exports, "__esModule", { value: true });
exports.MiliCron = void 0;
/**
 * Import the `EventEmitter` class from the `events` module.
 * @import { EventEmitter } from 'events'
 */
const events_1 = require("events");
/**
 * Import the `DateTime` class from the `luxon` module.
 * @import { DateTime } from 'luxon'
 */
const luxon_1 = require("luxon");
/**
 * A simple Cron Daemon which supports a resolution of up to 10ms.
 * @class MiliCron
 */
class MiliCron {
    /**
     * Creates a new instance of the MiliCron class.
     * @constructor
     * @param {boolean} [autostart=false] - Whether to automatically start the cron daemon upon instantiation.
     * @returns {MiliCron} - A new instance of the MiliCron class.
     */
    constructor(autostart) {
        _MiliCron_instances.add(this);
        _MiliCron_bus.set(this, void 0);
        _MiliCron_timeoutId.set(this, void 0);
        _MiliCron_running.set(this, false
        /**
         * Creates a new instance of the MiliCron class.
         * @constructor
         * @param {boolean} [autostart=false] - Whether to automatically start the cron daemon upon instantiation.
         * @returns {MiliCron} - A new instance of the MiliCron class.
         */
        );
        __classPrivateFieldSet(this, _MiliCron_bus, new events_1.EventEmitter({ captureRejections: true }), "f");
        __classPrivateFieldGet(this, _MiliCron_bus, "f").setMaxListeners(Infinity);
        if (autostart === true) {
            this.start();
        }
    }
    /**
     * Starts the cron daemon if it is not already running.
     * @function
     * @returns {void}
     */
    start() {
        if (__classPrivateFieldGet(this, _MiliCron_running, "f")) {
            return;
        }
        __classPrivateFieldGet(this, _MiliCron_instances, "m", _MiliCron_onTick).call(this);
    }
    /**
     * Stops the cron daemon if it is running.
     * @function
     * @returns {void}
     */
    stop() {
        if (__classPrivateFieldGet(this, _MiliCron_timeoutId, "f")) {
            clearTimeout(__classPrivateFieldGet(this, _MiliCron_timeoutId, "f"));
        }
        __classPrivateFieldSet(this, _MiliCron_running, false, "f");
    }
    /**
     * Stops the cron daemon if it is running and then starts it again.
     * @function
     * @returns {void}
     */
    restart() {
        this.stop();
        this.start();
    }
    /**
     * Returns a boolean indicating whether the cron daemon is currently running.
     * @readonly
     * @returns {boolean} - A boolean indicating whether the cron daemon is currently running.
     */
    get running() {
        return __classPrivateFieldGet(this, _MiliCron_running, "f");
    }
    $on(event, cb) {
        // TODO: Add validation to ensure that the event is a valid crontab and if we're dealing with a specific date/time, it should be in the future
        __classPrivateFieldGet(this, _MiliCron_bus, "f").on(event, cb);
    }
    $once(event, cb) {
        // TODO: Add validation to ensure that the event is a valid crontab and if we're dealing with a specific date/time, it should be in the future
        __classPrivateFieldGet(this, _MiliCron_bus, "f").once(event, cb);
    }
    $off(event, cb) {
        __classPrivateFieldGet(this, _MiliCron_bus, "f").off(event, cb);
    }
    /**
     * Removes all listeners for the specified event or all events if no event is specified.
     * @param {string} [event] - The name of the event to remove all listeners from.
     * @returns {void}
     * @see {@link events!EventEmitter.removeAllListeners | EventEmitter.removeAllListeners}
     */
    $clear(event) {
        if (event) {
            __classPrivateFieldGet(this, _MiliCron_bus, "f").removeAllListeners(event);
        }
        else {
            __classPrivateFieldGet(this, _MiliCron_bus, "f").removeAllListeners();
        }
    }
    /**
     * Determines whether the given crontab expression matches the given date and time.
     * @param {string} crontab - The crontab expression to check.
     * @param {DateTime} now - The date and time to check against the crontab expression.
     * @returns {boolean} - A boolean indicating whether the crontab expression matches the given date and time.
     */
    crontabMatchesDateTime(crontab, now) {
        return MiliCron.crontabMatchesDateTime(crontab, now);
    }
    /**
     * Parses the given cron expression and returns either a `DateTime` object representing the next time the cron expression will match, or a `CronTabObject` containing the parsed cron expression.
     * @param {string} cronExpression - The cron expression to parse.
     * @param {DateTime} [now] - The current date and time to use as a reference when calculating the next match time. If not provided, the current date and time will be used.
     * @throws {Error} - Throws an error if the cron expression is so invalid that it cannot be recognized, or if the `now` parameter is not a valid `DateTime` object.
     * @returns {DateTime | CronTabObject} - A `DateTime` object representing the next time the cron expression will match, or a `CronTabObject` containing the parsed cron expression.
     */
    getParsedCronExpression(cronExpression, now) {
        return MiliCron.getParsedCronExpression(cronExpression, now);
    }
    /**
     * Check if the crontab expression matches the current time
     * @param crontab A crontab expression, crontab alias or unix timestamp
     * @param now A DateTime object representing the current time
     * @returns If the crontab expression matches the current time
     */
    static crontabMatchesDateTime(crontab, now) {
        if (!now.isValid) {
            return false;
        }
        try {
            const cronTabObject = this.getParsedCronExpression(crontab, now);
            if (cronTabObject instanceof luxon_1.DateTime) {
                return (cronTabObject >= now.minus({ milliseconds: 9 }) &&
                    cronTabObject <= now.plus({ milliseconds: 9 }));
            }
            return [
                cronTabObject.millisecondly.has(now.millisecond),
                cronTabObject.secondly.has(now.second),
                cronTabObject.minutely.has(now.minute),
                cronTabObject.hourly.has(now.hour),
                cronTabObject.dayOfMonthly.has(now.day),
                cronTabObject.monthly.has(now.month),
                cronTabObject.dayOfWeekly.has(now.weekday),
            ].every((value) => value === true);
        }
        catch {
            return false;
        }
    }
    /**
     * Parses the given cron expression and returns either a `DateTime` object representing the next time the cron expression will match, or a `CronTabObject` containing the parsed cron expression.
     * @param {string} cronExpression - The cron expression to parse.
     * @param {DateTime} [now] - The current date and time to use as a reference when calculating the next match time. If not provided, the current date and time will be used.
     * @throws {Error} - Throws an error if the cron expression is so invalid that it cannot be recognized, or if the `now` parameter is not a valid `DateTime` object.
     * @returns {DateTime | CronTabObject} - A `DateTime` object representing the next time the cron expression will match, or a `CronTabObject` containing the parsed cron expression.
     */
    static getParsedCronExpression(cronExpression, now) {
        if (!now) {
            now = luxon_1.DateTime.now().toUTC();
        }
        else if (!now.isValid) {
            throw new Error('Invalid DateTime object');
        }
        // check if we're dealing with an aliased expression
        if (cronExpression.startsWith('@')) {
            const expression = __classPrivateFieldGet(this, _a, "a", _MiliCron_aliases_get).expressions[cronExpression];
            if (expression) {
                return this.getParsedCronExpression(expression);
            }
        }
        const crontabParts = cronExpression
            .replace(/\s+/g, ' ')
            .split(' ')
            .map((part) => part.trim());
        // if we're only dealing with 1 part, then we're dealing with a unix timestamp
        if (crontabParts.length === 1) {
            return luxon_1.DateTime.fromSeconds(parseInt(crontabParts[0]));
        }
        if (crontabParts.length > 7) {
            throw new Error('Invalid crontab expression');
        }
        while (crontabParts.length < 7) {
            crontabParts.unshift('0');
        }
        const [cronMil, cronSec, cronMin, cronHour, cronDayOfMonth, cronMonth, cronDayOfWeek] = crontabParts;
        const cronTabObject = {
            millisecondly: __classPrivateFieldGet(this, _a, "m", _MiliCron_parseField).call(this, 'millisecond', cronMil, __classPrivateFieldGet(this, _a, "a", _MiliCron_crontabContraints_get).millisecondly, now),
            secondly: __classPrivateFieldGet(this, _a, "m", _MiliCron_parseField).call(this, 'second', cronSec, __classPrivateFieldGet(this, _a, "a", _MiliCron_crontabContraints_get).secondly, now),
            minutely: __classPrivateFieldGet(this, _a, "m", _MiliCron_parseField).call(this, 'minute', cronMin, __classPrivateFieldGet(this, _a, "a", _MiliCron_crontabContraints_get).minutely, now),
            hourly: __classPrivateFieldGet(this, _a, "m", _MiliCron_parseField).call(this, 'hour', cronHour, __classPrivateFieldGet(this, _a, "a", _MiliCron_crontabContraints_get).hourly, now),
            dayOfMonthly: __classPrivateFieldGet(this, _a, "m", _MiliCron_parseField).call(this, 'dayOfMonth', cronDayOfMonth, __classPrivateFieldGet(this, _a, "a", _MiliCron_crontabContraints_get).dayOfMonthly, now),
            monthly: __classPrivateFieldGet(this, _a, "m", _MiliCron_parseField).call(this, 'month', cronMonth, __classPrivateFieldGet(this, _a, "a", _MiliCron_crontabContraints_get).monthly, now),
            dayOfWeekly: __classPrivateFieldGet(this, _a, "m", _MiliCron_parseField).call(this, 'dayOfWeek', cronDayOfWeek, __classPrivateFieldGet(this, _a, "a", _MiliCron_crontabContraints_get).dayOfWeekly, now),
        };
        return cronTabObject;
    }
}
exports.MiliCron = MiliCron;
_a = MiliCron, _MiliCron_bus = new WeakMap(), _MiliCron_timeoutId = new WeakMap(), _MiliCron_running = new WeakMap(), _MiliCron_instances = new WeakSet(), _MiliCron_onTick = function _MiliCron_onTick() {
    __classPrivateFieldSet(this, _MiliCron_running, true, "f");
    const now = luxon_1.DateTime.now().toUTC();
    const eventsWithListeners = __classPrivateFieldGet(this, _MiliCron_bus, "f").eventNames();
    // now we need to parse through all of the events to see if any of them match the current time
    eventsWithListeners
        .filter((e) => 'error' !== e)
        .forEach((eventName) => {
        if (MiliCron.crontabMatchesDateTime(eventName.toString(), now)) {
            __classPrivateFieldGet(this, _MiliCron_bus, "f").emit(eventName);
        }
    });
    __classPrivateFieldSet(this, _MiliCron_timeoutId, setTimeout(__classPrivateFieldGet(this, _MiliCron_instances, "m", _MiliCron_onTick).bind(this), 10), "f");
}, _MiliCron_crontabContraints_get = function _MiliCron_crontabContraints_get() {
    const constraints = {
        millisecondly: {
            min: 0,
            max: 999,
            chars: [],
            validChars: /^[,*\d/-]+$/,
        },
        secondly: {
            min: 0,
            max: 59,
            chars: [],
            validChars: /^[,*\d/-]+$/,
        },
        minutely: {
            min: 0,
            max: 59,
            chars: [],
            validChars: /^[,*\d/-]+$/,
        },
        hourly: {
            min: 0,
            max: 23,
            chars: [],
            validChars: /^[,*\d/-]+$/,
        },
        dayOfMonthly: {
            min: 1,
            max: 31,
            chars: ['L'],
            validChars: /^[?,*\dL/-]+$/,
        },
        monthly: {
            min: 1,
            max: 12,
            chars: [],
            validChars: /^[,*\d/-]+$/,
        },
        dayOfWeekly: {
            min: 0,
            max: 7,
            chars: ['L'],
            validChars: /^[?,*\dL#/-]+$/,
        },
    };
    Object.freeze(constraints);
    return constraints;
}, _MiliCron_aliases_get = function _MiliCron_aliases_get() {
    const aliases = {
        expressions: {
            '@yearly': '0 0 0 0 1 1 *',
            '@monthly': '0 0 0 0 1 * *',
            '@weekly': '0 0 0 0 * * 0',
            '@daily': '0 0 0 0 * * *',
            '@hourly': '0 0 0 * * * *',
        },
        month: {
            JAN: 1,
            FEB: 2,
            MAR: 3,
            APR: 4,
            MAY: 5,
            JUN: 6,
            JUL: 7,
            AUG: 8,
            SEP: 9,
            OCT: 10,
            NOV: 11,
            DEC: 12,
        },
        daysOfWeek: {
            SUN: 0,
            MON: 1,
            TUE: 2,
            WED: 3,
            THU: 4,
            FRI: 5,
            SAT: 6,
        },
    };
    Object.freeze(aliases);
    return aliases;
}, _MiliCron_parseField = function _MiliCron_parseField(field, value, constraints, now) {
    const ret = new Set();
    const aliases = __classPrivateFieldGet(this, _a, "a", _MiliCron_aliases_get)[field];
    switch (field) {
        case 'month':
        case 'dayOfWeek':
            value = value.replace(/[a-z]{3}/gi, (match) => {
                match = match.toUpperCase();
                if (!aliases) {
                    return 'X';
                }
                else if (typeof aliases[match] !== 'undefined') {
                    return aliases[match].toString();
                }
                else {
                    return 'X';
                }
            });
            break;
    }
    if (value === 'X' || !constraints.validChars.test(value)) {
        return ret;
    }
    if (value.includes('*')) {
        value = value.replace(/\*/g, constraints.min + '-' + constraints.max);
    }
    else if (value.includes('?')) {
        value = value.replace(/\?/g, constraints.min + '-' + constraints.max);
    }
    const atoms = value.split(',');
    atoms.forEach((atom) => {
        if (atom.includes('-')) {
            if (atom.includes('/')) {
                if (atom.split('/').length !== 2) {
                    return;
                }
                const [range, step] = atom.split('/');
                const [start, end] = range.split('-');
                const startNum = parseInt(start);
                const endNum = parseInt(end);
                const stepNum = parseInt(step);
                if (stepNum <= 0) {
                    return;
                }
                if (startNum > endNum) {
                    return;
                }
                for (let i = startNum; i <= endNum; i += stepNum) {
                    ret.add(i);
                }
            }
            else {
                const [start, end] = atom.split('-');
                const startNum = parseInt(start);
                const endNum = parseInt(end);
                if (startNum > endNum) {
                    return;
                }
                for (let i = startNum; i <= endNum; i++) {
                    ret.add(i);
                }
            }
        }
        else if (atom.includes('/')) {
            if (atom.split('/').length !== 2) {
                return;
            }
            const [start, step] = atom.split('/');
            const startNum = parseInt(start);
            const stepNum = parseInt(step);
            if (stepNum <= 0) {
                return;
            }
            if (startNum > constraints.max) {
                return;
            }
            for (let i = startNum; i <= constraints.max; i += stepNum) {
                ret.add(i);
            }
        }
        else if (atom.includes('#')) {
            const [dayOfWeek, weekOfMonth] = atom.split('#');
            const dayOfWeekNum = parseInt(dayOfWeek);
            const weekOfMonthNum = parseInt(weekOfMonth);
            if (dayOfWeekNum > constraints.max) {
                return;
            }
            const firstDayOfMonth = luxon_1.DateTime.fromObject({
                year: now.year,
                month: now.month,
                day: 1,
            });
            const firstDayOfMonthDayOfWeek = firstDayOfMonth.weekday;
            const firstDayOfWeek = luxon_1.DateTime.fromObject({
                year: now.year,
                month: now.month,
                day: 1 + (dayOfWeekNum - firstDayOfMonthDayOfWeek),
            });
            const nthDayOfWeek = firstDayOfWeek.plus({
                weeks: weekOfMonthNum - 1,
            });
            if (nthDayOfWeek.month !== now.month) {
                return;
            }
            ret.add(nthDayOfWeek.day);
        }
        else if (atom.includes('L')) {
            if (atom.split('/').length !== 2) {
                return;
            }
            const [start, step] = atom.split('/');
            const startNum = parseInt(start);
            const stepNum = parseInt(step);
            if (stepNum <= 0) {
                return;
            }
            if (startNum > constraints.max) {
                return;
            }
            for (let i = startNum; i <= constraints.max; i += stepNum) {
                ret.add(i);
            }
        }
        else {
            const num = parseInt(atom);
            if (num >= constraints.min && num <= constraints.max) {
                ret.add(num);
            }
        }
    });
    if (field === 'millisecond') {
        const current = [...ret];
        current.forEach((num) => {
            const toAddMin = num - 9 < constraints.min ? constraints.min : num - 9;
            const toAddMax = num + 9 > constraints.max ? constraints.max : num + 9;
            for (let i = toAddMin; i <= toAddMax; i++) {
                ret.add(i);
            }
        });
    }
    if (field === 'dayOfWeek') {
        if (ret.has(7)) {
            ret.add(0);
        }
        if (ret.has(0)) {
            ret.add(7);
        }
    }
    return ret;
};
/** {@inheritDoc MiliCron} */
exports.default = MiliCron;
//# sourceMappingURL=index.js.map